import { useLocation } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import {
  canOpenInSidebar,
  getInternalPathname,
} from "@apps/main/routes-constants";
import IntegrationLogo from "@components/integration-logo/integration-logo";
import AppLink from "@components/link/link";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { toWithBackground } from "@helpers/helpers";
import { pluralize } from "@helpers/string";

import {
  MessageSource,
  isSafeMessageSourceUrl,
  providerFromUrl,
} from "../helpers";

/**
 * Collapsed-by-default "N sources used" disclosure shown under an Ask AI answer.
 * Renders the model's <tf-sources> bullets as a compact list of links — internal
 * Topicflow links open in the sidebar, external ones in a new tab.
 */
const AskAIMessageSources = ({ sources }: { sources: MessageSource[] }) => {
  const location = useLocation<TFLocationState>();

  if (sources.length === 0) {
    return null;
  }

  return (
    <details className="group">
      <summary className="flex items-center gap-1.5 cursor-pointer list-none text-sm text-gray-400 hover:text-gray-600 select-none">
        <TopicflowIcon
          type="chevronRight"
          className="w-4 h-4 transition-transform group-open:rotate-90"
        />
        <span>
          {sources.length} {pluralize("source", sources.length)} used
        </span>
      </summary>
      <ul className="flex flex-col gap-1 mt-3 pl-1">
        {sources.map((source) => {
          const provider = providerFromUrl(source.url);
          const isSafeUrl = isSafeMessageSourceUrl(source.url);
          return (
            <li key={source.url} className="flex items-start gap-1.5 text-sm">
              {provider ? (
                <IntegrationLogo
                  provider={provider}
                  className="mt-0.5 w-4 h-4 shrink-0"
                />
              ) : (
                <TopicflowIcon
                  type="externalLink"
                  className="mt-1 w-3.5 h-3.5 shrink-0 text-gray-400"
                />
              )}
              {isSafeUrl && canOpenInSidebar(source.url) ? (
                <AppLink
                  to={toWithBackground({
                    pathname: getInternalPathname(source.url) || "",
                    location,
                  })}
                  className="text-gray-700 hover:underline"
                >
                  {source.title}
                </AppLink>
              ) : isSafeUrl ? (
                <a
                  href={source.url}
                  className="text-gray-700 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {source.title}
                </a>
              ) : (
                <span className="text-gray-700">{source.title}</span>
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
};

export default AskAIMessageSources;
